/**
 * Metadata eşleştirme diyaloğu (Tasarım V4, Öneri 13).
 *
 * Düşük güvenli eşleşmelerde yönetici buradan doğru TMDB kaydını seçer.
 * Sonuç seçimi `externalId` olarak döner; ağ isteği ebeveynde yapılır.
 * Görseller gösterilmez: dış görsel sunucusuna tarayıcıdan istek atılmaz.
 */
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { api, type MetadataCandidate } from '../api.ts';
import { CloseIcon, SearchIcon } from '../Icon.tsx';
import { useModalFocus } from '../useModalFocus.ts';
import { ErrorNotice } from './States.tsx';

export function MetadataMatchDialog({
  itemTitle,
  kind,
  busy,
  onSelect,
  onClose,
}: {
  itemTitle: string;
  kind: 'movie' | 'show';
  busy: boolean;
  onSelect: (externalId: string) => void;
  onClose: () => void;
}): ReactNode {
  const [query, setQuery] = useState(itemTitle);
  const [searchKind, setSearchKind] = useState<'movie' | 'show'>(kind);
  const [candidates, setCandidates] = useState<MetadataCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useModalFocus(dialogRef, onClose, searchRef);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api
      .searchMetadata(itemTitle, kind)
      .then((result) => {
        if (!cancelled) setCandidates(result.candidates);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Arama başarısız.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemTitle, kind]);

  const handleSearch = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await api.searchMetadata(query.trim(), searchKind);
      setCandidates(result.candidates);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Arama başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-lib-modal__backdrop">
      <button
        type="button"
        className="add-lib-modal__backdrop-btn"
        onClick={onClose}
        aria-label="Kapat"
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        className="add-lib-modal__card metadata-match"
        role="dialog"
        aria-modal="true"
        aria-labelledby="metadata-match-title"
        tabIndex={-1}
      >
        <div className="metadata-match__header">
          <div>
            <h2 id="metadata-match-title" className="metadata-match__title">
              Metadata Eşleştir
            </h2>
            <p className="metadata-match__desc">
              “{itemTitle}” için TMDB’de arayın ve doğru kaydı seçin.
            </p>
          </div>
          <button
            type="button"
            className="add-lib-modal__close-btn"
            onClick={onClose}
            aria-label="Kapat"
            title="Kapat"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <form className="metadata-match__form" onSubmit={(event) => void handleSearch(event)}>
          <input
            ref={searchRef}
            className="add-lib-modal__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Film veya dizi adı"
            aria-label="Metadata arama"
          />
          <select
            className="add-lib-modal__input metadata-match__kind"
            value={searchKind}
            onChange={(event) => setSearchKind(event.target.value as 'movie' | 'show')}
            aria-label="İçerik türü"
          >
            <option value="movie">Film</option>
            <option value="show">Dizi</option>
          </select>
          <button type="submit" className="button button--primary" disabled={loading || busy}>
            <SearchIcon size={15} />
            Ara
          </button>
        </form>

        {error !== null && <ErrorNotice message={error} />}

        {loading && (
          <p className="metadata-match__status" role="status">
            Aranıyor…
          </p>
        )}

        {!loading && candidates !== null && candidates.length === 0 && (
          <p className="metadata-match__status">Sonuç bulunamadı.</p>
        )}

        {!loading && candidates !== null && candidates.length > 0 && (
          <ul className="metadata-match__list">
            {candidates.map((candidate) => (
              <li key={candidate.externalId}>
                <button
                  type="button"
                  className="metadata-match__item"
                  onClick={() => onSelect(candidate.externalId)}
                  disabled={busy}
                >
                  <span className="metadata-match__item-title">
                    {candidate.title}
                    {candidate.year !== null ? ` (${candidate.year})` : ''}
                  </span>
                  {candidate.overview !== null && (
                    <span className="metadata-match__item-overview">{candidate.overview}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="metadata-match__actions">
          <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
}
